import { PropsWithChildren, createContext, useContext, useState } from 'react';

import { Product } from '../../../../common/models/product';
import { PurchaseOrderGenericItemType } from '../../models/purchase-order-generic-item';

interface PurchaseOrderDetailValue {
    /**
     * The supplier as the head of the document has it right now — undefined
     * while the head is not on screen, null when none is chosen.
     *
     * The article search has to follow the field, not the saved document:
     * since head and positions stand on one page, a new order has nothing
     * saved yet and a changed one would filter on the supplier it had before
     * (Prüfbericht Roland Niedermayr, 28.09.2026).
     */
    supplierAccountId?: number | null;
    setSupplierAccountId?: (id: number | null | undefined) => void;
    /** Its name, for the message when a search finds nothing. */
    supplierAccountName?: string;
    setSupplierAccountName?: (name: string | undefined) => void;
    /** How many positions the order carries right now, and how to empty it. */
    positionCount: number;
    setPositionCount?: (count: number) => void;
    removeAllPositions?: () => void;
    setRemoveAllPositions?: (remove: (() => void) | undefined) => void;
    showAddPositionModal: boolean;
    setShowAddPositionModal?: (show: boolean) => void;
    selectedProduct: Product | null;
    setSelectedProduct?: (product: Product | null) => void;
    showGenericItemModal: { show: boolean; type: PurchaseOrderGenericItemType };
    setShowGenericItemModal?: ({ show, type }: { show: boolean; type: PurchaseOrderGenericItemType }) => void;
}

export const PurchaseOrderDetailContext = createContext<PurchaseOrderDetailValue>({
    positionCount: 0,
    showAddPositionModal: false,
    selectedProduct: null,
    showGenericItemModal: { show: false, type: PurchaseOrderGenericItemType.Surcharge },
});

export const PurchaseOrderDetailProvider = (props: PropsWithChildren) => {
    const [supplierAccountId, setSupplierAccountId] = useState<number | null | undefined>(undefined);
    const [supplierAccountName, setSupplierAccountName] = useState<string | undefined>(undefined);
    const [positionCount, setPositionCount] = useState(0);
    const [removeAllPositions, setRemove] = useState<{ run?: () => void }>({});
    const [showAddPositionModal, setShowAddPositionModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showGenericItemModal, setShowGenericItemModal] = useState<{
        show: boolean;
        type: PurchaseOrderGenericItemType;
    }>({
        show: false,
        type: PurchaseOrderGenericItemType.Surcharge,
    });

    return (
        <PurchaseOrderDetailContext.Provider
            value={{
                supplierAccountId,
                setSupplierAccountId,
                supplierAccountName,
                setSupplierAccountName,
                positionCount,
                setPositionCount,
                removeAllPositions: removeAllPositions.run,
                setRemoveAllPositions: run => setRemove({ run }),
                showAddPositionModal,
                setShowAddPositionModal,
                selectedProduct,
                setSelectedProduct,
                showGenericItemModal,
                setShowGenericItemModal,
            }}
        >
            {props.children}
        </PurchaseOrderDetailContext.Provider>
    );
};

export const usePurchaseOrderDetailContext = () => {
    return useContext(PurchaseOrderDetailContext);
};
